import { useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Download, ShieldCheck, RotateCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api-client';
import { AuthShell } from '@/components/AuthShell';
import { CodeInput } from '@/components/CodeInput';

type Phase = 'scan' | 'recovery';

export function TotpSetup() {
  const navigate = useNavigate();
  const { state, totpSetup, totpConfirm, cancelLogin } = useAuth();
  const { t } = useTranslation('auth');

  const [phase, setPhase] = useState<Phase>('scan');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Retry-able fetch: bump `attempt` to refetch; the ref guards StrictMode's
  // double effect-run per attempt (the setup endpoint is rate-limited).
  const [attempt, setAttempt] = useState(0);
  const fetchedAttempt = useRef(-1);

  const inSetup = state.status === 'totp_setup';

  function describeError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 429) return err.message;
      if (err.status === 401 && /temp token/i.test(err.message)) return t('twofactor.sessionExpired');
    }
    return t('common:error');
  }

  useEffect(() => {
    if (!inSetup || fetchedAttempt.current === attempt) return;
    fetchedAttempt.current = attempt;
    setError('');
    totpSetup()
      .then((res) => {
        setQr(res.qrCodeDataUrl);
        setSecret(res.secret);
      })
      .catch((err: unknown) => setError(describeError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inSetup, attempt, totpSetup]);

  // After confirm we are 'authenticated' but still showing recovery codes —
  // only leave this screen once the user confirms they saved them.
  if (!inSetup && phase !== 'recovery') {
    if (state.status === 'authenticated') return <Navigate to="/" replace />;
    return <Navigate to="/login" replace />;
  }

  async function submitConfirm(value?: string) {
    const codeToUse = value ?? code;
    if (codeToUse.length !== 6 || isLoading) return;
    setError('');
    setIsLoading(true);
    try {
      const codes = await totpConfirm(codeToUse, secret);
      setRecoveryCodes(codes);
      setPhase('recovery');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 429 || (err.status === 401 && /temp token/i.test(err.message)))) {
        setError(describeError(err));
      } else {
        setError(t('twofactor.invalidCode'));
      }
      setCode('');
    } finally {
      setIsLoading(false);
    }
  }

  function copyCodes() {
    void navigator.clipboard.writeText(recoveryCodes.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function downloadCodes() {
    const blob = new Blob(
      [`UniPortal recovery codes\n\n${recoveryCodes.join('\n')}\n\nEach code can be used once.\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'uniportal-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (phase === 'recovery') {
    return (
      <AuthShell wide>
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-white/10 border border-white/[0.15]">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{t('totpSetup.recoveryTitle')}</h2>
            <p className="text-white/60 mt-1 text-sm">{t('totpSetup.recoverySubtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {recoveryCodes.map((c) => (
            <div
              key={c}
              className="px-2 py-2.5 rounded-lg bg-white/10 border border-white/[0.15] text-center font-mono text-sm text-white tracking-wider"
            >
              {c}
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={copyCodes} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-white/10 border border-white/[0.15] hover:bg-white/15 transition-colors">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t('totpSetup.copied') : t('totpSetup.copy')}
          </button>
          <button type="button" onClick={downloadCodes} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-white/10 border border-white/[0.15] hover:bg-white/15 transition-colors">
            <Download className="w-4 h-4" />
            {t('totpSetup.download')}
          </button>
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
            className="w-4 h-4 rounded accent-[#ed125f]"
          />
          <span className="text-sm text-white/70">{t('totpSetup.savedConfirm')}</span>
        </label>

        <button
          type="button"
          disabled={!saved}
          onClick={() => void navigate('/', { replace: true })}
          className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('totpSetup.continue')}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell wide>
      <div>
        <h2 className="text-xl font-bold text-white">{t('totpSetup.title')}</h2>
        <p className="text-white/60 mt-1 text-sm">{t('totpSetup.subtitle')}</p>
      </div>

      {error && (
        <div className="space-y-3">
          <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
            {error}
          </div>
          <div className="flex gap-3">
            {!secret && (
              <button
                type="button"
                onClick={() => setAttempt((a) => a + 1)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-white/10 border border-white/[0.15] hover:bg-white/15 transition-colors"
              >
                <RotateCw className="w-4 h-4" />
                {t('common:retry')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                cancelLogin();
                void navigate('/login');
              }}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white bg-white/5 border border-white/[0.15] hover:bg-white/10 transition-colors"
            >
              {t('twofactor.backToLogin')}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-6 items-center">
        <div className="shrink-0 p-3 rounded-2xl bg-white shadow-glass-lg">
          {qr ? (
            <img src={qr} alt="TOTP QR code" className="w-40 h-40" />
          ) : (
            <div className="w-40 h-40 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
            </div>
          )}
        </div>
        <div className="space-y-3 text-sm text-white/70">
          <p>{t('totpSetup.step1')}</p>
          {secret && (
            <div>
              <p className="text-xs text-white/50 mb-1">{t('totpSetup.manualEntry')}</p>
              <code className="block px-3 py-2 rounded-lg bg-white/10 border border-white/[0.15] font-mono text-xs text-white break-all select-all">
                {secret}
              </code>
            </div>
          )}
          <p>{t('totpSetup.step2')}</p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submitConfirm();
        }}
        className="space-y-5"
      >
        <div className="max-w-sm mx-auto w-full">
          <CodeInput value={code} onChange={setCode} onComplete={(v) => void submitConfirm(v)} disabled={isLoading || !secret} />
        </div>

        <button
          type="submit"
          disabled={isLoading || code.length !== 6}
          className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {isLoading ? t('totpSetup.confirming') : t('totpSetup.confirm')}
        </button>
      </form>
    </AuthShell>
  );
}
